/**
 * Markdown content for transliteration evaluation instructions
 */

export const instructionsMarkdown = `# How to Use Transliteration Ranking

## Overview

This tool helps evaluate machine-generated transliterations of names into various writing systems. You will review batches of 100 names, each with 5 transliteration options, and rank them from best to worst to help train our machine learning models.

**URL:** [https://www.familysearch.org/en/standards/admin/transliteration-eval](https://www.familysearch.org/en/standards/admin/transliteration-eval)

*Note: FamilySearch login required. This tool is for authorized language experts and contractors.*

---

## Getting Started

Input files will be provided to you in one of two ways:

- **Email:** You may receive a data file as an email attachment from FamilySearch
- **Web Links:** Or you may be directed to download from a link button on this page

Each batch contains **100 names** with **5 transliteration options** per name.

---

## Evaluation Workflow

1. **Step 1 - Load Your Batch:** Click "Load New Batch" and select the JSON file you received (or drag & drop it)

2. **Step 2 - Review Each Name:** For each of the 100 names in the batch:
   - View the original name (Latin script)
   - View the IPA (International Phonetic Alphabet) pronunciation
   - See 5 transliteration candidates in the target writing system

3. **Step 3 - Rank All 5 Options:** Drag the candidates to order them from best (rank #1 at top) to worst (rank #5 at bottom)
   - *Important: All 5 options must be ranked, even if some seem equally good or bad*
   - Use your language expertise to determine the most accurate transliteration
   - Consider pronunciation accuracy, writing conventions, and readability

4. **Step 4 - Navigate Through Batch:** Use "Next" button to proceed (use "Previous" to go back if needed)

5. **Step 5 - Complete All 100 Names:** Evaluate all names in the batch to reach 100/100

6. **Step 6 - Finish Evaluation:** On the 100th name, click "Finish" to complete your evaluation

7. **Step 7 - Download Results:** After clicking Finish, click "Download" to save your evaluation as JSON

8. **Step 8 - Email Results:** Reply to the original email that sent you the batch, attaching your downloaded JSON file

---

## Evaluation Guidelines

When ranking the 5 transliteration options, consider:

- **Pronunciation Accuracy:** How closely does the transliteration match the IPA pronunciation?
- **Native Writing Conventions:** Does it follow standard spelling rules and conventions of the target script?
- **Readability:** Would a native speaker easily recognize and pronounce this correctly?
- **Common Usage:** Does it align with how similar names are typically written?

*Remember: Even if multiple options seem similar, you must rank all 5 from best to worst based on your language expertise.*

---

## How to Rank

- **Mouse/Trackpad:** Click and hold the drag handle (⋮⋮) or anywhere on the candidate card, then drag up/down to reorder
- **Touch Devices:** Tap and hold a candidate, then drag your finger up/down to change its position
- **Keyboard Shortcuts:** Click on a candidate to focus it, then use:
  - \`↑\` / \`↓\` arrows - Move up/down by one position
  - \`1\` through \`5\` keys - Jump directly to that rank
- **Visual Feedback:** The rank badge turns green for #1 (best). Changed positions show "was #X" indicator

---

## Multiple Sessions

**You don't need to complete all 100 names in one sitting!**

- **Automatic Saving:** Your progress is automatically saved to your computer's browser storage after each evaluation
- **Resume Anytime:** Close the browser and come back later - your work will be preserved for up to 24 hours
- **Position Remembered:** The tool will remember which name you were on and restore all your previous rankings
- **Navigate Freely:** Use Previous/Next buttons to review and modify earlier evaluations before finishing

*Note: Data is stored locally in your browser only. If you clear your browser cache or switch computers, you will need to start over.*

---

## File Format

Input files are in **JSON format** and contain names with IPA pronunciations and transliteration candidates.

**Example structure:**

\`\`\`json
{
  "names": [
    {
      "original": "Smith",
      "ipa": "smɪθ",
      "candidates": [
        { "text": "สมิธ", "score": 0.95 },
        { "text": "สมิท", "score": 0.82 }
      ]
    }
  ]
}
\`\`\`

Candidates can include optional beam scores which will be preserved in the output for ML training.

---

## Downloading & Submitting Results

After evaluating all 100 names:

1. Click **"Finish"** on the last item to complete your evaluation
2. Then click **"Download"** to save your results
   - A JSON file will automatically download to your computer
   - The filename will include a timestamp (e.g., "transliteration-eval-2026-03-31.json")
   - This file contains:
     - All 100 names with your evaluated rankings
     - Pairwise comparisons for machine learning training
     - Original beam scores (if provided)
     - Timestamp and your evaluator email address

**After downloading:**

1. **Reply to the original email** from the person who sent you the batch
2. **Attach the JSON file** you just downloaded
3. Add any comments or questions about the batch if needed
4. Send the email - you're done!

---

## Additional Features

- **Progress Indicator:** Shows your current position (e.g., "45 / 100") and percentage complete
- **Two-Step Completion:** Finish button marks completion, then Download button saves results to ensure deliberate submission
- **Keyboard Accessible:** Full keyboard support for navigation and ranking
- **Mobile Friendly:** Works on tablets and phones with touch support
- **Visual Confirmation:** Changed rankings show indicators so you can see what you've modified

---

## Important Reminders

- **Rank All 5:** Every name must have all 5 options ranked, even if differences are subtle
- **Complete All 100:** You must evaluate all names in the batch before the Finish button appears
- **Two Steps to Submit:** Click "Finish" first, then click "Download" to save your results
- **Email Results Back:** Reply to the sender's email with your downloaded JSON file attached
- **24-Hour Limit:** Cached work expires after 24 hours of inactivity, so plan accordingly
- **Don't Clear Cache:** Avoid clearing your browser cache until after you've submitted your results

---

## Troubleshooting

### Lost my progress:
- Progress is stored in your browser. Make sure you're using the same browser and computer
- Check that you haven't cleared your browser cache or browsing data
- Data expires after 24 hours of inactivity

### Can't upload my file:
- Ensure the file is in JSON format (must have .json extension)
- File should not exceed 10MB
- Check that the file structure matches the expected format (see File Format section above)

### Finish button not appearing:
- You must evaluate all 100 names first
- Use the progress indicator to see how many remain
- Navigate through the batch to ensure each name has been ranked (even if you didn't change the order)

### Download button not showing:
- First click "Finish" on the 100th item
- The "Download" button appears after you click "Finish"

### Need help?
- Contact the person who sent you the evaluation batch
- Include details about what you were doing when the issue occurred
- Mention your browser (Chrome, Firefox, Safari, etc.) and device type
`
